import React, { useState, useEffect, useRef } from "react";
import { Connector, Account, ConnectFormResponse, OAuthAccountCandidate } from "../types";
import { 
    getConnectors, getAccounts, getConnectForm, connectAccount, updateAccount, deleteAccount,
    startOAuth, checkOAuthStatus, finalizeOAuth
} from "../services/api";
import { 
    Plus, X, Loader2, Settings, Link2, Trash2, Zap, ShieldCheck, 
    Globe, ExternalLink, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Users, Check, Info
} from "lucide-react";
import { Loader } from "./Loader";

interface ConnectorsListProps {
  onAccountsUpdated: () => void;
}

export const ConnectorsList: React.FC<ConnectorsListProps> = ({ onAccountsUpdated }) => {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Record<string, Account[]>>({});
  
  // UI State
  const [expandedConnectorId, setExpandedConnectorId] = useState<string | null>(null);

  // Form State (Create/Edit)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(null);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ConnectFormResponse | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // OAuth State
  const [oauthProcessId, setOauthProcessId] = useState<string | null>(null);
  const [oauthStatus, setOauthStatus] = useState<'idle' | 'polling' | 'awaiting_selection' | 'completed' | 'failed'>('idle');
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthCandidates, setOauthCandidates] = useState<OAuthAccountCandidate[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Management Modal State
  const [manageAccount, setManageAccount] = useState<{connector: Connector, account: Account} | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const currentConnector = connectors.find(c => c.id === selectedConnectorId);

  useEffect(() => {
    loadData();
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (!isFormOpen) stopPolling();
  }, [isFormOpen]);

  useEffect(() => {
    if (!manageAccount) setShowDeleteConfirm(false);
  }, [manageAccount]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getConnectors();
      setConnectors(res.result);
      
      const accs: Record<string, Account[]> = {};
      
      await Promise.all(res.result.map(async (conn) => {
        try {
          const accRes = await getAccounts(conn.id);
          if (accRes.result && Array.isArray(accRes.result.accounts)) {
             accs[conn.id] = accRes.result.accounts;
          } else {
             accs[conn.id] = [];
          }
        } catch (e) {
          accs[conn.id] = [];
        }
      }));
      
      setAccounts(accs);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
    }
    setOauthProcessId(null);
    setOauthStatus('idle');
    setOauthError(null);
    setOauthCandidates([]);
    setSelectedCandidates(new Set());
  };

  const toggleExpand = (id: string) => {
      setExpandedConnectorId(prev => prev === id ? null : id);
  };

  const handleOpenConnect = async (connectorId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setFormLoading(true);
    setSelectedConnectorId(connectorId);
    setEditingAccountId(null);
    setFormError(null);
    stopPolling();

    try {
      const res = await getConnectForm(connectorId);
      setFormData(res.result);
      setFormValues({});
      setIsFormOpen(true);
    } catch (e) {
      alert("Не удалось загрузить форму подключения");
    } finally {
      setFormLoading(false);
    }
  };

  const executeDelete = async () => {
      if (!manageAccount) return;
      setActionLoading(true);
      try {
          await deleteAccount(manageAccount.connector.id, manageAccount.account.id);
          setManageAccount(null);
          await loadData();
          onAccountsUpdated();
      } catch (e) {
          alert("Не удалось удалить интеграцию");
      } finally {
          setActionLoading(false);
      }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConnectorId) return;

    setFormLoading(true);
    setFormError(null);
    try {
      if (editingAccountId) {
          await updateAccount(selectedConnectorId, editingAccountId, formValues);
      } else {
          await connectAccount(selectedConnectorId, formValues);
      }
      setIsFormOpen(false);
      await loadData();
      onAccountsUpdated();
      setExpandedConnectorId(selectedConnectorId);
    } catch (err: any) {
      setFormError(err.message || "Ошибка при сохранении данных аккаунта");
    } finally {
      setFormLoading(false);
    }
  };

  const handleOAuthStart = async () => {
      if (!selectedConnectorId) return;
      setFormLoading(true);
      setOauthError(null);
      try {
          const res = await startOAuth(selectedConnectorId);
          setOauthProcessId(res.processId);
          window.open(res.authUrl, '_blank', 'width=600,height=700,status=yes,scrollbars=yes');
          setOauthStatus('polling');
          startPolling(res.processId);
      } catch (e) {
          setOauthError("Не удалось инициализировать вход через OAuth.");
          setOauthStatus('failed');
      } finally {
          setFormLoading(false);
      }
  };

  const startPolling = (pid: string) => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = setInterval(async () => {
          try {
              const res = await checkOAuthStatus(pid);
              if (res.result.status === 'awaiting_selection') {
                  if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
                  setOauthCandidates(res.result.data?.accounts || []);
                  setOauthStatus('awaiting_selection');
              } 
              else if (res.result.status === 'completed') {
                  if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
                  setOauthStatus('completed');
                  setTimeout(async () => {
                      setIsFormOpen(false);
                      await loadData();
                      onAccountsUpdated();
                      setExpandedConnectorId(selectedConnectorId);
                      stopPolling();
                  }, 1500);
              } 
              else if (res.result.status === 'failed') {
                  if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
                  setOauthStatus('failed');
                  setOauthError(res.result.error || "Авторизация не удалась");
              }
          } catch (e) {}
      }, 2000);
  };

  const handleOAuthSelection = async () => {
      if (!oauthProcessId || selectedCandidates.size === 0) return;
      setFormLoading(true);
      try {
          await finalizeOAuth(oauthProcessId, Array.from(selectedCandidates));
          setOauthStatus('polling');
          startPolling(oauthProcessId);
      } catch (e) {
          setOauthError("Не удалось сохранить выбранные аккаунты");
          setOauthStatus('failed');
      } finally {
          setFormLoading(false);
      }
  };

  const toggleCandidate = (id: string) => {
      const next = new Set(selectedCandidates);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedCandidates(next);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-[2rem] p-8 text-white shadow-lg shadow-blue-200">
          <h2 className="text-3xl font-black mb-2 tracking-tight">Каналы связи</h2>
          <p className="text-blue-100 max-w-2xl font-medium opacity-90">
              Подключите ваши социальные сети и мессенджеры для автоматической публикации контента.
          </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {loading ? (
             Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 rounded-xl bg-slate-200 animate-pulse" />
                            <div className="space-y-2">
                                <div className="h-5 w-32 bg-slate-200 rounded animate-pulse" />
                                <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
                            </div>
                        </div>
                        <div className="h-8 w-24 bg-slate-200 rounded animate-pulse" />
                    </div>
                </div>
            ))
        ) : (
            connectors.map((conn) => {
            const connAccounts = accounts[conn.id] || [];
            const count = connAccounts.length;
            const isExpanded = expandedConnectorId === conn.id;

            return (
                <div key={conn.id} className={`bg-white rounded-[1.5rem] border transition-all duration-300 overflow-hidden ${isExpanded ? 'border-blue-200 shadow-xl ring-1 ring-blue-100 scale-[1.01]' : 'border-slate-200 shadow-sm hover:border-blue-300 hover:shadow-md'}`}>
                    <div onClick={() => toggleExpand(conn.id)} className="p-5 flex items-center justify-between cursor-pointer select-none bg-white">
                        <div className="flex items-center space-x-4">
                            <div className="relative">
                                <img src={conn.logoUrl} alt={conn.displayName} className="w-12 h-12 rounded-2xl object-contain bg-slate-50 border border-slate-100 p-1" />
                                {conn.authMode === 'oauth' && (
                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center" title="OAuth">
                                        <Globe className="w-2.5 h-2.5 text-white" />
                                    </div>
                                )}
                            </div>
                            <div>
                                <h3 className="font-black text-lg text-slate-800 tracking-tight">{conn.displayName}</h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                    {count > 0 ? (
                                        <span className="text-[10px] font-black text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex items-center uppercase tracking-widest">
                                            <Users className="w-3 h-3 mr-1" /> {count} акк.
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Нет подключений</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button onClick={(e) => handleOpenConnect(conn.id, e)} className="hidden sm:flex items-center px-4 py-2 bg-blue-50 text-blue-700 text-xs font-black rounded-xl hover:bg-blue-100 transition-all active:scale-95 uppercase tracking-widest">
                                <Plus className="w-3.5 h-3.5 mr-1.5" />
                                Добавить
                            </button>
                            <div className={`p-2 rounded-full transition-all ${isExpanded ? 'bg-slate-100 text-slate-600 rotate-180' : 'text-slate-400'}`}>
                                <ChevronDown className="w-5 h-5" />
                            </div>
                        </div>
                    </div>
                    
                    <div className={`transition-all duration-300 ease-in-out border-t border-slate-50 bg-slate-50/30 ${isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden border-none'}`}>
                        <div className="p-5">
                            {count === 0 ? (
                                <div className="text-center py-8 text-slate-400">
                                    <p className="mb-4 text-sm font-medium">Аккаунты еще не добавлены</p>
                                    <button onClick={(e) => handleOpenConnect(conn.id, e)} className="inline-flex items-center px-6 py-3 bg-white border border-slate-200 text-slate-800 font-black text-xs rounded-2xl hover:bg-slate-50 hover:border-blue-300 transition-all shadow-sm uppercase tracking-widest">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Подключить {conn.displayName}
                                    </button>
                                </div>
                            ) : (
                                <div className="grid gap-3 sm:grid-cols-2">
                                    {connAccounts.map(acc => (
                                        <div key={acc.id} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between group hover:border-blue-300 transition-all">
                                            <div className="flex items-center space-x-4 overflow-hidden">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center font-black text-slate-400 text-sm border border-slate-100 shadow-inner">
                                                    {(acc.credentials.displayName?.[0] || acc.externalId?.[0] || "?").toUpperCase()}
                                                </div>
                                                <div className="truncate">
                                                    <div className="font-black text-slate-800 text-sm truncate w-full tracking-tight">
                                                        {acc.credentials.displayName || acc.credentials.meta?.botUsername || "Account"}
                                                    </div>
                                                    <div className="text-[10px] text-slate-400 font-mono opacity-70">ID: {acc.externalId}</div>
                                                </div>
                                            </div>
                                            <button onClick={(e) => { e.stopPropagation(); setManageAccount({connector: conn, account: acc}); }} className="p-2.5 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all" title="Настройки">
                                                <Settings className="w-4.5 h-4.5" />
                                            </button>
                                        </div>
                                    ))}
                                    <button onClick={(e) => handleOpenConnect(conn.id, e)} className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-blue-400 hover:bg-white hover:text-blue-600 transition-all gap-2 text-[10px] font-black uppercase tracking-[0.2em]">
                                        <Plus className="w-5 h-5" />
                                        Добавить еще
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
            })
        )}
      </div>

      {/* Manage Account Modal */}
      {manageAccount && (
        <div className="fixed top-[-100px] left-0 right-0 bottom-[-100px] z-[99999] flex items-center justify-center bg-slate-900/50 backdrop-blur-lg p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-200">
                <div className="bg-slate-50/80 px-8 py-6 border-b border-slate-100 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-600/10 rounded-2xl text-blue-600">
                             <Settings className="w-6 h-6" />
                        </div>
                        <h3 className="font-black text-xl text-slate-900 tracking-tight">Управление</h3>
                    </div>
                    <button onClick={() => setManageAccount(null)} className="p-2 text-slate-400 hover:text-rose-500 bg-white border border-slate-100 rounded-xl transition-all shadow-sm active:scale-90"><X className="w-5 h-5"/></button>
                </div>
                
                <div className="p-10">
                    <div className="flex items-center gap-6 bg-slate-50 p-6 rounded-[2rem] border border-slate-100 mb-8 shadow-inner">
                        <div className="w-16 h-16 bg-white rounded-[1.5rem] p-3 shadow-sm border border-slate-100 flex items-center justify-center">
                             <img src={manageAccount.connector.logoUrl} className="max-w-full max-h-full" alt=""/>
                        </div>
                        <div className="overflow-hidden">
                            <div className="font-black text-slate-900 text-lg truncate tracking-tight leading-tight mb-1">
                                {manageAccount.account.credentials.displayName || manageAccount.account.credentials.meta?.botUsername || "Account"}
                            </div>
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ID: {manageAccount.account.externalId}</div>
                        </div>
                    </div>

                    {showDeleteConfirm ? (
                        <div className="bg-rose-50 border border-rose-100 rounded-[2rem] p-6 animate-in fade-in zoom-in-95">
                            <div className="flex items-start gap-4 mb-6">
                                <div className="p-3 bg-rose-100 rounded-2xl text-rose-600 flex-shrink-0 shadow-sm">
                                    <AlertTriangle className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-black text-rose-900 text-base">Удалить интеграцию?</h4>
                                    <p className="text-xs text-rose-700/80 mt-1 leading-relaxed font-medium">
                                        Связь с социальной сетью будет разорвана. Это действие нельзя отменить.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => setShowDeleteConfirm(false)} disabled={actionLoading} className="flex-1 py-4 bg-white border border-rose-200 text-slate-700 font-black rounded-2xl hover:bg-slate-50 transition-all text-xs uppercase tracking-widest">
                                    Отмена
                                </button>
                                <button 
                                    onClick={executeDelete} 
                                    disabled={actionLoading} 
                                    className="flex-1 py-4 bg-rose-600 text-white font-black rounded-2xl transition-all flex items-center justify-center shadow-[0_4px_0_0_rgba(190,18,60,1)] active:shadow-none active:translate-y-[4px] text-xs uppercase tracking-widest"
                                >
                                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Удалить"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                             <button onClick={() => setShowDeleteConfirm(true)} className="w-full flex items-center p-5 rounded-[1.5rem] border-2 border-slate-100 hover:border-rose-500 hover:bg-rose-50/30 transition-all group text-left">
                                 <div className="bg-rose-100 text-rose-600 p-3.5 rounded-xl mr-5 group-hover:bg-rose-600 group-hover:text-white transition-colors">
                                     <Trash2 className="w-5 h-5" />
                                 </div>
                                 <div>
                                     <div className="font-black text-slate-800 tracking-tight">Удалить аккаунт</div>
                                     <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Отключить навсегда</div>
                                 </div>
                             </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* Connection Form Modal (Standard or OAuth) */}
      {isFormOpen && formData && (
        <div className="fixed top-[-100px] left-0 right-0 bottom-[-100px] z-[99999] flex items-center justify-center bg-slate-900/50 backdrop-blur-lg p-4 animate-in fade-in duration-300 pb-[100px]">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden transform transition-all animate-in zoom-in-95 duration-300 border border-slate-200/60 ring-1 ring-white/10">
            <div className="bg-slate-50/80 px-8 py-7 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center space-x-5">
                <div className="bg-blue-600 p-3.5 rounded-2xl shadow-xl shadow-blue-600/30">
                    <Link2 className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h3 className="font-black text-xl text-slate-900 tracking-tight leading-none">
                        {editingAccountId ? "Изменение" : "Подключение"}
                    </h3>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1.5">Параметры доступа</p>
                </div>
              </div>
              <button onClick={() => setIsFormOpen(false)} className="p-2.5 text-slate-300 hover:text-rose-500 bg-white border border-slate-100 rounded-2xl transition-all shadow-sm active:scale-90">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-8 lg:p-10 overflow-y-auto max-h-[80vh] custom-scrollbar">
                <div className="flex items-start gap-4 text-sm text-slate-600 bg-blue-50/50 p-5 rounded-2xl border border-blue-100/50 mb-8">
                    <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="leading-relaxed font-semibold opacity-90">
                        {formData.description}
                    </p>
                </div>

                {oauthStatus !== 'idle' ? (
                    <div className="text-center py-4 space-y-6">
                        {oauthStatus === 'polling' && (
                            <div className="py-8">
                                <div className="relative w-20 h-20 mx-auto mb-8">
                                    <div className="absolute inset-0 border-[6px] border-slate-100 rounded-full"></div>
                                    <div className="absolute inset-0 border-[6px] border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                                </div>
                                <div>
                                    <h4 className="font-black text-xl text-slate-900 tracking-tight">Ожидаем вход...</h4>
                                    <p className="text-slate-500 text-sm mt-3 max-w-[280px] mx-auto leading-relaxed font-bold">
                                        Завершите авторизацию в открывшемся окне браузера. Это окно обновится автоматически.
                                    </p>
                                </div>
                            </div>
                        )}
                        
                        {oauthStatus === 'awaiting_selection' && (
                            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <h4 className="font-black text-xl text-slate-900 mb-2 tracking-tight">Выберите страницы</h4>
                                <p className="text-slate-500 text-[10px] mb-6 font-black uppercase tracking-widest opacity-60">Отметьте нужные аккаунты для связи</p>
                                
                                <div className="max-h-64 overflow-y-auto custom-scrollbar border border-slate-100 rounded-2xl mb-8 bg-slate-50 shadow-inner p-1">
                                    {oauthCandidates.map(cand => {
                                        const isSelected = selectedCandidates.has(cand.id);
                                        return (
                                            <div 
                                                key={cand.id}
                                                onClick={() => toggleCandidate(cand.id)}
                                                className={`p-4 flex items-center rounded-xl mb-1 cursor-pointer transition-all ${isSelected ? 'bg-white shadow-md' : 'hover:bg-white/60'}`}
                                            >
                                                <div className={`w-6 h-6 rounded-lg border-2 mr-4 flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-600 scale-110 shadow-md shadow-blue-600/20' : 'border-slate-300'}`}>
                                                    {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                                </div>
                                                <div className="text-left overflow-hidden">
                                                    <div className={`font-black text-sm truncate tracking-tight ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>{cand.name}</div>
                                                    <div className="text-[9px] text-slate-400 font-mono font-bold mt-0.5">ID: {cand.id}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={handleOAuthSelection}
                                    disabled={selectedCandidates.size === 0 || formLoading}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl transition-all flex items-center justify-center shadow-[0_4px_0_0_rgba(29,78,216,1)] active:shadow-none active:translate-y-[4px] disabled:opacity-50 disabled:shadow-none disabled:translate-y-0 uppercase tracking-[0.2em] text-xs"
                                >
                                    {formLoading ? <Loader2 className="w-5 h-5 animate-spin"/> : `Принять (${selectedCandidates.size})`}
                                </button>
                            </div>
                        )}

                        {oauthStatus === 'completed' && (
                            <div className="animate-in zoom-in duration-500 py-10">
                                <div className="w-20 h-20 bg-green-100 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-sm">
                                    <CheckCircle className="w-10 h-10 text-green-600" />
                                </div>
                                <h4 className="font-black text-2xl text-slate-900 tracking-tight">Успех!</h4>
                                <p className="text-slate-500 font-black mt-2 uppercase tracking-[0.2em] text-[9px] opacity-60">Аккаунт успешно привязан</p>
                            </div>
                        )}

                        {oauthStatus === 'failed' && (
                            <div className="animate-in zoom-in duration-500 py-10">
                                <div className="w-20 h-20 bg-rose-100 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-sm">
                                    <AlertTriangle className="w-10 h-10 text-rose-600" />
                                </div>
                                <h4 className="font-black text-xl text-slate-900 tracking-tight">Ошибка входа</h4>
                                <p className="text-rose-600 text-xs mt-4 px-6 bg-rose-50 py-4 rounded-2xl border border-rose-100 font-bold leading-relaxed">{oauthError}</p>
                                <button 
                                    onClick={handleOAuthStart}
                                    className="mt-8 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black px-6 py-3 rounded-xl uppercase tracking-[0.2em] text-[10px] transition-all"
                                >
                                    Попробовать снова
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <form onSubmit={handleFormSubmit} className="space-y-6">
                        {formError && (
                            <div className="bg-rose-50 border border-rose-100 rounded-2xl p-5 flex items-start animate-in shake duration-500">
                                <AlertTriangle className="w-5 h-5 text-rose-500 mr-4 flex-shrink-0 mt-0.5" />
                                <div className="text-rose-700 text-[11px] font-black leading-relaxed italic">
                                    {formError}
                                </div>
                            </div>
                        )}

                        <div className="space-y-5">
                            {formData.fields.map((field) => {
                                if (field.type === 'button') {
                                    return (
                                        <div key={field.name} className="py-1">
                                            <button
                                                type="button"
                                                onClick={handleOAuthStart}
                                                disabled={formLoading}
                                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl transition-all flex items-center justify-center shadow-[0_4px_0_0_rgba(29,78,216,1)] active:shadow-none active:translate-y-[4px] group uppercase tracking-[0.2em] text-xs"
                                            >
                                                {formLoading ? (
                                                    <Loader2 className="w-5 h-5 animate-spin mr-3"/>
                                                ) : (
                                                    <>
                                                        {currentConnector?.logoUrl ? (
                                                            <img src={currentConnector.logoUrl} alt="" className="w-6 h-6 mr-3 rounded-lg bg-white p-0.5 group-hover:scale-110 transition-transform" />
                                                        ) : (
                                                            <ExternalLink className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" />
                                                        )}
                                                    </>
                                                )}
                                                {field.label || "Авторизоваться"}
                                            </button>
                                        </div>
                                    );
                                }

                                return (
                                    <div key={field.name} className="space-y-2">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">
                                            {field.label} {field.required && <span className="text-rose-500 ml-1">*</span>}
                                        </label>
                                        <div className="relative group">
                                            <input
                                                type={field.type === 'password' ? 'password' : 'text'}
                                                required={field.required}
                                                className="w-full px-6 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:ring-8 focus:ring-blue-500/5 focus:border-blue-500 focus:bg-white transition-all text-slate-900 font-bold text-sm placeholder:text-slate-300 shadow-inner"
                                                placeholder={field.description}
                                                value={formValues[field.name] || ""}
                                                onChange={(e) => {
                                                    setFormValues({...formValues, [field.name]: e.target.value});
                                                    setFormError(null);
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {!formData.fields.some(f => f.type === 'button') && (
                            <div className="pt-6">
                                <button
                                    type="submit"
                                    disabled={formLoading}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl transition-all flex items-center justify-center shadow-[0_4px_0_0_rgba(29,78,216,1)] active:shadow-none active:translate-y-[4px] disabled:opacity-50 disabled:shadow-none disabled:translate-y-0 uppercase tracking-[0.2em] text-xs"
                                >
                                    {formLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingAccountId ? "Сохранить изменения" : "Подключить аккаунт")}
                                </button>
                            </div>
                        )}
                    </form>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};